"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { CommonDescription } from "../../component/CommonDescription/CommonDescription";
import { Dropdown } from "../../component/Dropdown/Dropdown";
import { colorSettingDetection, darkTextureWrapper, renderHeaderConditionally } from "../../utils/functions";
import { useScreenSize } from "../../utils/hooks/useScreenSize";
import FeatureRow, { FeatureRowProps } from "./FeatureRow";
import { layoutHeaderClasses, layoutHeaderBigClasses, layoutSubheaderClasses, layoutEyebrowClasses } from "@/components/tailwindHelpers";
import arrowUpDark from "../../images/arrow-up--dark.svg";
import arrowUpLight from "../../images/arrow-up--light.svg";
import FeatureRowMobile from "./FeatureRowMobile";
export type Product = {
  id: string;
  name: string;
  description?: string;
  iconUrl?: string;
};
export type LayoutComparisonTableProps = {
  eyebrow?: string;
  header: string;
  subheader?: string;
  isHeaderBig?: boolean;
  isWrapped?: boolean;
  headerElement?: "h1" | "h2";
  presetTheme?: string;
  description?: string;
  featureColumnTitle: string;
  products: Product[]; // up to 10; only 3 columns are shown at once (see MAX_VISIBLE_COLUMNS)
  features: FeatureRowProps[];
  footerLinkText?: string;
};
const MAX_VISIBLE_COLUMNS = 3;
// A table showing fewer than two products has nothing to compare, so the last
// two selections can't be cleared.
const MIN_SELECTED_PRODUCTS = 2;
const productLabelClasses = "text-gray-m10 text-[1.375rem] not-italic font-bold leading-7 mb-2";
const productDescriptionClasses = "text-[#464b52] text-base not-italic font-normal leading-6 tracking-[0.16px]";
const productPlaceholderClasses = "text-[#464b52] text-base not-italic font-normal leading-6 tracking-[0.16px]";
const backToTopClasses = "flex items-center gap-2 py-6 px-5 text-blue-trmb uppercase no-underline text-lg not-italic font-bold leading-7 tracking-[1.44px] max-sm:text-sm max-sm:leading-[1.125rem] max-sm:tracking-[0.7px] hover:underline focus:!font-bold focus:outline-none focus:underline active:!font-bold active:outline-none active:underline";
export const LayoutComparisonTable = t0 => {
  const $ = _c(45);
  const {
    eyebrow,
    header,
    subheader,
    isHeaderBig,
    headerElement: t1,
    isWrapped,
    presetTheme,
    description,
    featureColumnTitle,
    products,
    features,
    footerLinkText
  } = t0;
  const headerElement = t1 === undefined ? "h2" : t1;
  const generateStableId = _temp;
  const uniqueId = generateStableId(header);
  const {
    width
  } = useScreenSize();
  const isMobile = width <= 600;
  const visibleColumnCount = Math.min(MAX_VISIBLE_COLUMNS, products.length);
  const showColumnPicker = products.length > MAX_VISIBLE_COLUMNS;
  let t2;
  if ($[0] !== products || $[1] !== visibleColumnCount) {
    t2 = () => products.slice(0, visibleColumnCount).map(_temp2);
    $[0] = products;
    $[1] = visibleColumnCount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [selectedProductIds, setSelectedProductIds] = useState(t2);
  let t3;
  if ($[3] !== products || $[4] !== selectedProductIds) {
    let t4;
    if ($[6] !== products) {
      t4 = id => id ? products.findIndex(product_0 => product_0.id === id) : -1;
      $[6] = products;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t3 = selectedProductIds.map(t4);
    $[3] = products;
    $[4] = selectedProductIds;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const selectedIndexes = t3;
  let t4;
  if ($[8] !== selectedProductIds) {
    t4 = selectedProductIds.some(_temp3);
    $[8] = selectedProductIds;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const hasSelectedProduct = t4;
  let t5;
  if ($[10] !== selectedProductIds) {
    t5 = selectedProductIds.filter(Boolean);
    $[10] = selectedProductIds;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const canClearSelection = t5.length > MIN_SELECTED_PRODUCTS;
  const handleColumnSelect = (columnIndex, selected) => {
    if (selectedProductIds[columnIndex] === selected[0]) {
      handleColumnClear(columnIndex);
    } else {
      setSelectedProductIds(prev => {
        const next = [...prev];
        next[columnIndex] = selected[0];
        return next;
      });
    }
  };
  const handleColumnClear = columnIndex_0 => {
    if (!canClearSelection) {
      return;
    }
    setSelectedProductIds(prev_0 => {
      const next_0 = [...prev_0];
      next_0[columnIndex_0] = undefined;
      return next_0;
    });
  };
  let t6;
  if ($[12] !== products || $[13] !== selectedProductIds) {
    t6 = columnIndex_1 => products.filter(product_1 => product_1.id === selectedProductIds[columnIndex_1] || !selectedProductIds.includes(product_1.id)).map(_temp4);
    $[12] = products;
    $[13] = selectedProductIds;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const getColumnDropdownOptions = t6;
  const scrollContentRef = useRef(null);
  const scrollbarRef = useRef(null);
  const thumbRef = useRef(null);
  let t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const content = scrollContentRef.current;
      const thumb = thumbRef.current;
      const scrollbar = scrollbarRef.current;
      if (!content || !thumb || !scrollbar) {
        return;
      }
      let rafId = null;
      const handleScroll = () => {
        if (rafId !== null) {
          return;
        }
        rafId = requestAnimationFrame(() => {
          const scrollableWidth = content.scrollWidth - content.clientWidth;
          const scrollRatio = scrollableWidth > 0 ? content.scrollLeft / scrollableWidth : 0;
          const thumbWidth = content.clientWidth / content.scrollWidth * scrollbar.clientWidth;
          thumb.style.width = `${thumbWidth / 1.7}px`;
          thumb.style.left = `${scrollRatio * (scrollbar.clientWidth - thumbWidth) * 3.2}px`;
          rafId = null;
        });
      };
      content.addEventListener("scroll", handleScroll, {
        passive: true
      });
      handleScroll();
      return () => {
        content.removeEventListener("scroll", handleScroll);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    };
    t8 = [];
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  const wrapperClasses = `${isWrapped ? "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : ""}`;
  const t9 = presetTheme || "";
  let t10;
  if ($[17] !== t9) {
    t10 = colorSettingDetection(t9);
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const colorSetting = t10;
  const mainClasses = `py-[100px] ${presetTheme || ""} ${colorSetting}`;
  const headerClasses = isHeaderBig ? layoutHeaderBigClasses : layoutHeaderClasses;
  const t11 = header || "";
  let t12;
  if ($[19] !== headerClasses || $[20] !== headerElement || $[21] !== t11) {
    t12 = renderHeaderConditionally(headerClasses, t11, headerElement);
    $[19] = headerClasses;
    $[20] = headerElement;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const finalHeader = t12;
  const t13 = presetTheme || "";
  let t14;
  if ($[23] !== t13) {
    t14 = darkTextureWrapper(t13);
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const componentStyles = t14;
  const t15 = `comparison-table-top-${uniqueId}`;
  const t16 = "layout-comparison-table";
  let t17;
  if ($[25] !== eyebrow) {
    t17 = eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>;
    $[25] = eyebrow;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== subheader) {
    t18 = subheader && <div className={layoutSubheaderClasses}>{subheader}</div>;
    $[27] = subheader;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== description) {
    t19 = description && <CommonDescription description={description} className="t2-layout__description" />;
    $[29] = description;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== finalHeader || $[32] !== t17 || $[33] !== t18 || $[34] !== t19) {
    t20 = <div className="t2-layout__content"><div className="mb-20">{t17}{finalHeader}{t18}{t19}</div></div>;
    $[31] = finalHeader;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  const t21 = isMobile ? <div className="overflow-x-auto" ref={scrollContentRef}><div className={showColumnPicker ? "min-w-full bg-white w-full" : "w-[120%] min-w-full bg-white"}><div className="overflow-x-auto"><div className="overflow-x-auto min-h-[400px]"><div className="flex justify-between border border-gray-m2 border-b-gray-m10 rounded-t-[10px] bg-white">{showColumnPicker ? Array.from({
              length: visibleColumnCount
            }, (_, index) => {
              const productIndex = selectedIndexes[index];
              const product_3 = productIndex >= 0 ? products[productIndex] : undefined;
              const isLastColumn = index === visibleColumnCount - 1;
              const dropdownPickerClasses = `!w-auto [&_li]:p-2 [&_li]:text-sm [&_li]:leading-4 [&_li]:gap-x-1 min-w-[150px] ${isLastColumn ? "right-0 left-auto" : ""}`;
              return <div key={`product-column-${index}`} data-testid={`comparison-product-column-${index}`} className="w-1/3 text-center py-2 px-1 md:p-2"><div className="mb-2 flex justify-center"><Dropdown label={product_3 ? product_3.name : "Select product"} options={getColumnDropdownOptions(index)} selectedFilters={selectedProductIds[index] ? [selectedProductIds[index]] : []} multiSelect={false} resetAllLabel="Clear selection" onChange={selected_0 => handleColumnSelect(index, selected_0)} onResetAll={canClearSelection ? () => handleColumnClear(index) : undefined} inputClasses="justify-between max-sm:text-xs max-sm:!px-1 max-sm:!py-2" wrapperClasses="min-w-auto" dropdownClasses={dropdownPickerClasses} /></div>{product_3 && !showColumnPicker && <><div className={productLabelClasses} data-testid={`comparison-product-name-${index}`}>{product_3.name}</div>{product_3.description && <small className={productDescriptionClasses}>{product_3.description}</small>}</>}</div>;
            }) : products.map(_temp5)}</div>{!showColumnPicker && <div className="h-2 bg-[#fafafa] overflow-hidden w-full relative border-l border-r border-gray-m2" ref={scrollbarRef}><div className="absolute h-full bg-[#c1c1c1] rounded left-0 transition-[left] duration-100 ease-linear" ref={thumbRef} /></div>}{hasSelectedProduct && <div>{features.map((feature, index_1) => <FeatureRowMobile key={`feature-row-${index_1}`} feature={feature} selectedIndexes={selectedIndexes} />)}</div>}<div className="border border-gray-m2 border-t-gray-m10 rounded-b-[10px] bg-white">{footerLinkText && <a href={`#comparison-table-top-${uniqueId}`} className={backToTopClasses}><Image src={colorSetting === "t2-color-setting--dark" ? arrowUpDark : arrowUpLight} alt="Back to top" />{footerLinkText}</a>}</div></div></div></div></div> : <div className="w-full overflow-x-auto min-h-[400px]"><table className="w-full border-separate border-spacing-0 border border-gray-m2 rounded-[10px] overflow-visible table-fixed bg-white"><thead><tr><th className="py-2 px-6 text-left align-middle border-b border-gray-m10 w-[400px] font-medium rounded-tl-[10px] text-gray-m10 text-[1.375rem] leading-7">{featureColumnTitle}</th>{showColumnPicker ? Array.from({
            length: visibleColumnCount
          }, (__0, index_2) => {
            const productIndex_0 = selectedIndexes[index_2];
            const product_5 = productIndex_0 >= 0 ? products[productIndex_0] : undefined;
            return <th key={`product-column-${index_2}`} data-testid={`comparison-product-column-${index_2}`} className={`py-2 px-6 text-center align-middle border-b border-gray-m10 w-[247px] ${index_2 === visibleColumnCount - 1 ? "rounded-tr-[10px]" : ""}`}><div className="mb-2 flex justify-center"><Dropdown label={product_5 ? product_5.name : "Select product"} options={getColumnDropdownOptions(index_2)} selectedFilters={selectedProductIds[index_2] ? [selectedProductIds[index_2]] : []} multiSelect={false} resetAllLabel="Clear selection" onChange={selected_1 => handleColumnSelect(index_2, selected_1)} onResetAll={canClearSelection ? () => handleColumnClear(index_2) : undefined} inputClasses="justify-between text-normal" /></div>{product_5 && !showColumnPicker && <><div className={productLabelClasses} data-testid={`comparison-product-name-${index_2}`}>{product_5.name}</div>{product_5.description && <small className={productDescriptionClasses}>{product_5.description}</small>}</>}</th>;
          }) : products.map((product_6, index_3) => <th key={`product-${product_6.id}-${index_3}`} className={`py-2 px-6 text-center align-middle border-b border-gray-m10 w-[247px] ${index_3 === products.length - 1 ? "rounded-tr-[10px]" : ""}`}><div className={productLabelClasses}>{product_6.name}</div>{product_6.description && <small className={productDescriptionClasses}>{product_6.description}</small>}</th>)}</tr></thead><tbody className="[&>tr:last-child>td]:!border-b-[#252a2e]">{hasSelectedProduct && features.map((feature_0, index_4) => <FeatureRow key={`feature-${index_4}`} feature={feature_0} selectedIndexes={selectedIndexes} />)}</tbody><tfoot><tr><td className="!p-0">{footerLinkText && <a href={`#comparison-table-top-${uniqueId}`} className={backToTopClasses}><Image src={colorSetting === "t2-color-setting--dark" ? arrowUpDark : arrowUpLight} alt="Back to top" />{footerLinkText}</a>}</td></tr></tfoot></table></div>;
  let t22;
  if ($[36] !== t20 || $[37] !== t21 || $[38] !== wrapperClasses) {
    t22 = <div className={wrapperClasses}>{t20}{t21}</div>;
    $[36] = t20;
    $[37] = t21;
    $[38] = wrapperClasses;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== componentStyles || $[41] !== mainClasses || $[42] !== t15 || $[43] !== t22) {
    t23 = <div className={mainClasses} style={componentStyles} id={t15} data-testid={t16}>{t22}</div>;
    $[40] = componentStyles;
    $[41] = mainClasses;
    $[42] = t15;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  return t23;
};
function _temp(text) {
  if (!text) {
    return "comparison-table-unknown";
  }
  const slug = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return `comparison-table-${slug}`;
}
function _temp2(product) {
  return product.id;
}
function _temp3(id_0) {
  return Boolean(id_0);
}
function _temp4(product_2) {
  return {
    label: product_2.name,
    value: product_2.id
  };
}
function _temp5(product_4, index_0) {
  return <div key={`product-${product_4.id}-${index_0}`} className="w-1/3 text-center p-2"><div className={productLabelClasses}>{product_4.name}</div>{product_4.description && <small className={productDescriptionClasses}>{product_4.description}</small>}</div>;
}